import {
    AlternateEmail, Build, Category, Contacts, ContentPasteGo, Email, Feed, Gite, Laptop, Notes, Person,
    QuestionMark, Restore, Router, SettingsEthernet
} from "@mui/icons-material";
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedUser from "@mui/icons-material/VerifiedUser";
import { Button, Divider, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, styled } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import getEnvironment from "../../env";
import { PermissionEnum } from "../../feature/Permission/PermissionEnum";
import { useGetServicesByOrganisationAndApproverQuery } from "../../feature/ServiceApi/redux/serviceApiSlice";
import { AbilityContext, Can } from "../../feature/UserApi/logic/Can";
import { UserContext } from "../../feature/UserApi/logic/FetchUser";
import { PaginationQueryPackage } from "../../models/API/QueryParams/PaginationQueryPackage";
import OrganisationPicker, { OrganisationContext } from "./OrganisationPicker";
import DnsIcon from '@mui/icons-material/Dns';

interface TopBarProps {
    drawerWidth: number
}

export const SideMenu = (props: TopBarProps) => {
    const { t } = useTranslation()
    const organisationContext = useContext(OrganisationContext)?.organisation
    const userContext = useContext(UserContext);
    const ability = useContext(AbilityContext);
    const location = useLocation();

    const isSdnReadOrAdmin = userContext?.allowEmptyOrganisation();


    const approverServicesResult = useGetServicesByOrganisationAndApproverQuery({
        pagination: new PaginationQueryPackage(),
        uuid: organisationContext?.uuid
    }, { skip: organisationContext === undefined });

    const approverServices = approverServicesResult.data ?? [];

    const SideMenuItem = styled(ListItem)({
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 3,
        paddingBottom: 3,
    });

    const SideMenuIcon = styled(ListItemIcon)({
        marginLeft: 5,
    });

    const isSelected = (path: string) => location.pathname === path || location.pathname.includes(path + "/");

    return (
        <Drawer
            sx={{
                width: props.drawerWidth,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: props.drawerWidth,
                    boxSizing: 'border-box',
                },
            }}
            variant="permanent"
            anchor="left"
        >

            <Button component={Link} to="/">
                <img style={{ height: "3.6em" }} src={"/medcom_logo.png"} alt="medcom" />
            </Button>

            <Divider />
            <List style={{ padding: 0 }}>
                <OrganisationPicker />
                <Can I={PermissionEnum.SERVICE_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/services")} component={Link} color="inherit" to="/services">
                            <SideMenuIcon>
                                <Category />
                            </SideMenuIcon>
                            <ListItemText primary={t("Services") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.CLIENT_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/clients")} component={Link} color="inherit" to="/clients">
                            <SideMenuIcon>
                                <Laptop />
                            </SideMenuIcon>
                            <ListItemText primary={t("Clients") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                {
                    (ability.can(PermissionEnum.SERVICE_READ, organisationContext) || ability.can(PermissionEnum.CLIENT_READ, organisationContext) || approverServices.length > 0) &&
                    <>
                        <SideMenuItem>
                            <ListItemButton selected={isSelected("/agreementrequests")} component={Link} color="inherit" to="/agreementrequests">
                                <SideMenuIcon>
                                    <ContentPasteGo />
                                </SideMenuIcon>
                                <ListItemText primary={t("Agreement requests") + ""} />
                            </ListItemButton>
                        </SideMenuItem>
                        <SideMenuItem>
                            <ListItemButton selected={isSelected("/agreements")} component={Link} color="inherit" to="/agreements">
                                <SideMenuIcon>
                                    <HandshakeIcon />
                                </SideMenuIcon>
                                <ListItemText primary={t("Agreements") + ""} />
                            </ListItemButton>
                        </SideMenuItem>
                    </>
                }

                <Can I={PermissionEnum.SERVICE_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/service-windows")} component={Link} color="inherit" to="/service-windows">
                            <SideMenuIcon>
                                <Build />
                            </SideMenuIcon>
                            <ListItemText primary={t("Service Windows") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.HOSTING_SITE_READ} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/hosting-sites")} component={Link} color="inherit" to="/hosting-sites">
                            <SideMenuIcon>
                                <Gite />
                            </SideMenuIcon>
                            <ListItemText primary={t("Hosting sites") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.USER_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/users")} component={Link} color="inherit" to="/users">
                            <SideMenuIcon>
                                <Person />
                            </SideMenuIcon>
                            <ListItemText primary={t("Users") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.AUTHORISATION_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/authorisations")} component={Link} color="inherit" to="/authorisations">
                            <SideMenuIcon>
                                <VerifiedUser />
                            </SideMenuIcon>
                            <ListItemText primary={t("Authorisations") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.EXTERNAL_CONTACT_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/contacts")} component={Link} color="inherit" to="/contacts">
                            <SideMenuIcon>
                                <AlternateEmail />
                            </SideMenuIcon>
                            <ListItemText primary={t("Contacts") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                <Can I={PermissionEnum.DNS_RECORD_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/dns")} component={Link} color="inherit" to="/dns" style={{ paddingRight: 0, overflow: 'hidden' }}>
                            <SideMenuIcon>
                                <DnsIcon />
                            </SideMenuIcon>
                            <ListItemText primary={t("DNS")} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>

                <Can I={PermissionEnum.ORGANISATION_READ} a={organisationContext} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/organisation")} component={Link} color="inherit" to="/organisation" style={{ paddingRight: 0, overflow: 'hidden' }}>
                            <SideMenuIcon>
                                <BusinessIcon />
                            </SideMenuIcon>
                            <ListItemText primary={t("Organisation information")} />
                        </ListItemButton>
                    </SideMenuItem>
                    <Divider />
                </Can>
                <Divider />
                <Can I={PermissionEnum.AUDIT_READ} >
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/events")} component={Link} color="inherit" to="/events">
                            <SideMenuIcon>
                                <Restore />
                            </SideMenuIcon>
                            <ListItemText primary={t("Events") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                </Can>
                {isSdnReadOrAdmin &&
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/portaliases")} component={Link} color="inherit" to="/portaliases">
                            <SideMenuIcon>
                                <SettingsEthernet />
                            </SideMenuIcon>
                            <ListItemText primary={t("Portaliases") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                }
                {isSdnReadOrAdmin &&
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/ipinfo")} component={Link} color="inherit" to="/ipinfo">
                            <SideMenuIcon>
                                <Router />
                            </SideMenuIcon>
                            <ListItemText primary={t("IP info") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                }
                {isSdnReadOrAdmin &&
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/contactlabels")} component={Link} color="inherit" to="/contactlabels">
                            <SideMenuIcon>
                                <Contacts />
                            </SideMenuIcon>
                            <ListItemText primary={t("Contactlabels") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                }
                {isSdnReadOrAdmin &&
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/mail-list")} component={Link} color="inherit" to="/mail-list">
                            <SideMenuIcon>
                                <Email />
                            </SideMenuIcon>
                            <ListItemText primary={t("Mail overview") + ""} />
                        </ListItemButton>
                    </SideMenuItem>
                }
                <Divider />
                <>
                    <SideMenuItem>
                        <ListItemButton selected={isSelected("/support")} component={Link} color="inherit" to="/support">
                            <SideMenuIcon>
                                <QuestionMark />
                            </SideMenuIcon>
                            <ListItemText primary={t("Troubleshooting and support")} />
                        </ListItemButton>
                    </SideMenuItem>
                    <SideMenuItem>
                        <ListItemButton component={Link} color="inherit" to={getEnvironment().REACT_APP_SDN_DOCUMENTATION_LINK} target='_blank'>
                            <SideMenuIcon>
                                <Notes />
                            </SideMenuIcon>
                            <ListItemText primary={t("Agreementportal documentation")} />
                        </ListItemButton>
                    </SideMenuItem>
                    <SideMenuItem>
                        <ListItemButton component={Link} color="inherit" to={getEnvironment().REACT_APP_SDN_ACCESSIBILITY_LINK} target='_blank'>
                            <SideMenuIcon>
                                <Feed />
                            </SideMenuIcon>
                            <ListItemText primary={t("Accessibility Statement")} />
                        </ListItemButton>
                    </SideMenuItem>
                </>
            </List>
        </Drawer >
    )
}